import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import moment from 'moment';

const NICFOST_OFFICE_ADDRESS = 'No. 11 Dire Dawa Street, Wuse Zone 6, Abuja';

// Canonical status values used in props:
//   regStatus / licenseStatus: 'pending' | 'submitted' | 'approved' | 'rejected' | 'returned' | null
//   paymentStatus: 'uninitialized' | 'initialized' | 'pending_payment' | 'pending_confirmation'
//                 | 'complete' | 'waived' | 'failed' | 'hash_error' | null

const REGISTRY_ACTION = {
  label: 'View Registry',
  href: 'https://nicfost.gov.ng/public-registry/',
  external: true
};

const APPLY_LICENSE_ACTION = {
  label: 'Apply for License',
  to: '/register-license'
};

const PAYMENT_PENDING_STATUSES = new Set([
  'uninitialized',
  'initialized',
  'pending_payment'
]);

const PAYMENT_FAILED_STATUSES = new Set([
  'failed',
  'hash_error'
]);

const StatusBanner = ({
  licenseStatus,
  paymentStatus,
  licenseExpired,
  licenseExpDate,
  licenseId,
  hasLicenseApplication,
  hasLicenseCertificate,
  licenseOfficialRemarks,
  onPayment,
  regStatus,
  regOfficialRemarks,
  isVerifyingPayments,
  certificateIssued,
  hasRegistrationCertificate
}) => {
  const [, setForceRender] = useState(0);

  if (isVerifyingPayments) {
    return (
      <div className="status-banner status-info">
        <div className="banner-content">
          <div className="banner-title">
            <i className="material-icons spinning">sync</i>
            Verifying Payment Status...
          </div>
          <div className="banner-message">
            Please wait while we verify your payment information.
          </div>
        </div>
      </div>
    );
  }

  // First match wins. Higher-urgency situations come first.
  const getBannerConfig = () => {
    const licenseStatusLower = licenseStatus?.toLowerCase();
    const regStatusLower = regStatus?.toLowerCase();

    // ===== License-state banners =====

    // 1. License expired
    if (licenseExpired) {
      const daysExpired = licenseExpDate
        ? Math.ceil((new Date() - new Date(licenseExpDate)) / (1000 * 60 * 60 * 24))
        : null;
      const hasPenalty = daysExpired !== null && daysExpired > 90;
      const penaltyDeadline = licenseExpDate
        ? moment(licenseExpDate).add(90, 'days').format('D MMMM YYYY')
        : null;

      let message;
      if (hasPenalty) {
        message = `Your license expired ${daysExpired} days ago. Penalty fees apply. You are no longer listed in the NiCFoST Public Registry.`;
      } else if (penaltyDeadline) {
        message = `Your license has expired. Renew by ${penaltyDeadline} to avoid penalties. You are no longer listed in the NiCFoST Public Registry.`;
      } else {
        message = 'Your license has expired. Renew now to restore your listing in the NiCFoST Public Registry.';
      }

      return {
        type: 'danger',
        title: 'License Expired',
        message,
        action: {
          label: hasPenalty ? 'Renew with Penalty' : 'Renew Now',
          to: `/new-renew-license/${licenseId}`
        },
        secondaryAction: REGISTRY_ACTION
      };
    }

    // 2. License expiring soon (≤ 60 days, not yet expired)
    if (
      licenseStatusLower === 'approved' &&
      !licenseExpired &&
      licenseExpDate
    ) {
      const daysUntilExpiry = Math.ceil(
        (new Date(licenseExpDate) - new Date()) / (1000 * 60 * 60 * 24)
      );

      if (daysUntilExpiry > 0 && daysUntilExpiry <= 60) {
        const expiryFormatted = moment(licenseExpDate).format('D MMMM YYYY');
        return {
          type: 'warning',
          title: 'License Expiring Soon',
          message: `Your license will expire on ${expiryFormatted} (${daysUntilExpiry} days). Renew early to avoid penalties. Once expired, you will be removed from the NiCFoST Public Registry.`,
          action: {
            label: 'Renew Now',
            to: `/new-renew-license/${licenseId}`
          }
        };
      }
    }

    // 3. License payment processing
    if (licenseStatusLower === 'pending' && paymentStatus === 'pending_confirmation') {
      return {
        type: 'info',
        title: 'Payment Processing',
        message: 'Your payment is being confirmed. This may take a few minutes. You will be notified once confirmed.',
        action: {
          label: 'View Status',
          to: '/pending-payment'
        }
      };
    }

    // 4. License payment failed (Remita rejection or hash error) — user must retry
    if (licenseStatusLower === 'pending' && PAYMENT_FAILED_STATUSES.has(paymentStatus)) {
      return {
        type: 'warning',
        title: 'Payment Failed',
        message:
          paymentStatus === 'hash_error'
            ? 'We could not verify your last payment due to a security check failure. Please retry the payment.'
            : 'Your last payment attempt failed. Please retry to complete your license application.',
        action: {
          label: 'Retry Payment',
          onClick: onPayment
        }
      };
    }

    // 5. License payment required
    if (licenseStatusLower === 'pending' && PAYMENT_PENDING_STATUSES.has(paymentStatus)) {
      return {
        type: 'info',
        title: 'Payment Required',
        message: 'Your license application is pending payment. Complete payment to proceed.',
        action: {
          label: 'Pay Now',
          onClick: onPayment
        }
      };
    }

    // 6. License returned for corrections
    if (licenseStatusLower === 'returned') {
      return {
        type: 'warning',
        title: 'Action Required: License Returned',
        message: licenseOfficialRemarks
          ? `Your license application needs corrections: ${licenseOfficialRemarks}`
          : 'Your license application has been returned for correction. Please review the feedback and resubmit.',
        action: {
          label: 'Make Corrections',
          to: '/register-license'
        }
      };
    }

    // 7. License rejected
    if (licenseStatusLower === 'rejected') {
      return {
        type: 'danger',
        title: 'License Application Rejected',
        message: licenseOfficialRemarks
          ? `Your license application was rejected: ${licenseOfficialRemarks}`
          : 'Your license application has been rejected. Please contact support for more information.',
        action: {
          label: 'Contact Support',
          href: 'mailto:support@nicfost.gov.ng'
        }
      };
    }

    // 8. License submitted - awaiting admin review
    if (licenseStatusLower === 'submitted') {
      return {
        type: 'info',
        title: 'License Application Under Review',
        message: 'Your license application has been submitted and is being reviewed. You will be notified once a decision is made.',
        action: {
          label: 'View License History',
          to: '/license-history'
        }
      };
    }

    // 9. License approved but certificate not yet issued
    if (licenseStatusLower === 'approved' && hasLicenseCertificate === false) {
      return {
        type: 'info',
        title: 'License Approved - Certificate Pending',
        message: `Congratulations! Your license has been approved. Your certificate will be issued soon and you will be notified when it is ready for download. If your certificate was issued physically, it can be collected at the NiCFoST Office: ${NICFOST_OFFICE_ADDRESS}.`,
        action: {
          label: 'View License History',
          to: '/license-history'
        }
      };
    }

    // ===== Registration-state banners (only reached when license is in a benign or absent state) =====

    // 10. Registration returned for corrections
    if (regStatusLower === 'returned') {
      return {
        type: 'warning',
        title: 'Action Required: Registration Returned',
        message: regOfficialRemarks
          ? `Your registration needs corrections: ${regOfficialRemarks}`
          : 'Your registration has been returned for correction. Please review and resubmit.',
        action: {
          label: 'Make Corrections',
          to: '/register-food-scientist-v3'
        }
      };
    }

    // 11. Registration rejected
    if (regStatusLower === 'rejected') {
      return {
        type: 'danger',
        title: 'Registration Rejected',
        message: regOfficialRemarks
          ? `Your registration was rejected: ${regOfficialRemarks}`
          : 'Your registration has been rejected. Please contact support for more information.',
        action: {
          label: 'Contact Support',
          href: 'mailto:support@nicfost.gov.ng'
        }
      };
    }

    // 12. Registration submitted - awaiting admin review
    if (regStatusLower === 'submitted') {
      return {
        type: 'info',
        title: 'Registration Under Review',
        message: 'Your registration has been submitted and is being reviewed. You will be notified once approved.'
        // No action — user has nothing to do but wait.
      };
    }

    // 13. Registration pending payment — user abandoned mid-registration
    if (regStatusLower === 'pending') {
      return {
        type: 'warning',
        title: 'Registration Pending Payment',
        message: 'Your registration is incomplete. Resume the registration to complete payment and submit for review.',
        action: {
          label: 'Complete Registration',
          to: '/register-food-scientist-v3'
        }
      };
    }

    // 14. Registration approved, certificate not yet issued.
    //     If the user hasn't applied for a license yet, prompt them to do so —
    //     they don't need to wait for the registration certificate.
    //     If a license application is already in flight, there is no in-app
    //     action they can take here (the cert is an admin task), so the banner
    //     is purely informational.
    if (regStatusLower === 'approved' && certificateIssued === false) {
      if (!hasLicenseApplication) {
        return {
          type: 'info',
          title: 'Registration Approved - Certificate Pending',
          message: 'Congratulations! Your registration has been approved. Your certificate will be issued soon and you will be notified when it is ready for download. In the meantime, you can apply for your Annual Practice License.',
          action: APPLY_LICENSE_ACTION,
          secondaryAction: REGISTRY_ACTION
        };
      }
      return {
        type: 'info',
        title: 'Registration Approved - Certificate Pending',
        message: 'Congratulations! Your registration has been approved. Your certificate will be issued soon and you will be notified when it is ready for download.'
        // No action — admin task, license app already in flight.
      };
    }

    // 15. Registration approved, certificate issued physically (no digital file).
    //     If no license application yet, prompt for it.
    //     Otherwise informational only — collecting the physical cert happens
    //     at the NiCFoST office, not in-app.
    if (
      regStatusLower === 'approved' &&
      certificateIssued === true &&
      hasRegistrationCertificate === false
    ) {
      if (!hasLicenseApplication) {
        return {
          type: 'info',
          title: 'Get Your Practice License',
          message: `Your registration certificate was issued physically. If not yet collected, visit the NiCFoST Office: ${NICFOST_OFFICE_ADDRESS}. You can now apply for your Annual Practice License to legally practice as a Food Scientist in Nigeria.`,
          action: APPLY_LICENSE_ACTION,
          secondaryAction: REGISTRY_ACTION
        };
      }
      return {
        type: 'info',
        title: 'Registration Certificate Issued (Physical)',
        message: `Your registration certificate has been issued physically. If not yet collected, it can be collected at the NiCFoST Office: ${NICFOST_OFFICE_ADDRESS}.`
        // No action — collection is offline.
      };
    }

    // 16. Registration approved, no license application yet
    if (!hasLicenseApplication && regStatusLower === 'approved') {
      return {
        type: 'info',
        title: 'Get Your Practice License',
        message: 'Obtain your Annual Practice License to legally practice as a Food Scientist in Nigeria. A license is required to work in Government, Academia, Industry, and Research positions. A valid license also lists you in the NiCFoST Public Registry.',
        action: APPLY_LICENSE_ACTION,
        secondaryAction: REGISTRY_ACTION
      };
    }

    // 17. Happy path — valid license, listed in registry
    if (licenseStatusLower === 'approved' && !licenseExpired && licenseExpDate) {
      const expiryFormatted = moment(licenseExpDate).format('D MMMM YYYY');
      return {
        type: 'info',
        title: "You're Listed in the Public Registry",
        message: `Your valid license (expires ${expiryFormatted}) means you are publicly listed and verifiable in the NiCFoST Public Registry.`,
        action: REGISTRY_ACTION
      };
    }

    return null;
  };

  const config = getBannerConfig();

  if (!config) return null;

  // Only purely informational banners are dismissable. Warnings and dangers
  // always require user attention until the underlying state changes.
  const isDismissable = config.type === 'info';

  const dismissKey = `banner_dismissed_${config.title}`;
  const dismissedTime = isDismissable ? localStorage.getItem(dismissKey) : null;
  const isDismissed = dismissedTime &&
    (Date.now() - parseInt(dismissedTime)) / (1000 * 60 * 60) < 24;

  if (isDismissed) return null;

  const handleDismiss = () => {
    localStorage.setItem(dismissKey, Date.now().toString());
    setForceRender(n => n + 1);
  };

  const renderPrimaryAction = (action) => {
    if (!action) return null;
    if (action.to) {
      return (
        <Link to={action.to} className="banner-btn banner-btn-primary">
          {action.label}
        </Link>
      );
    }
    if (action.href) {
      return (
        <a
          href={action.href}
          className="banner-btn banner-btn-primary"
          {...(action.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
        >
          {action.label}
        </a>
      );
    }
    return (
      <button
        onClick={action.onClick}
        className="banner-btn banner-btn-primary"
      >
        {action.label}
      </button>
    );
  };

  return (
    <div className={`status-banner status-${config.type}`}>
      <div className="banner-content">
        <div className="banner-title">
          <i className="material-icons">
            {config.type === 'danger' ? 'error' :
             config.type === 'warning' ? 'warning' : 'info'}
          </i>
          {config.title}
        </div>
        <div className="banner-message">{config.message}</div>
      </div>

      {(config.action || config.secondaryAction) && (
        <div className="banner-actions">
          {renderPrimaryAction(config.action)}
          {config.secondaryAction && (
            <a
              href={config.secondaryAction.href}
              className="banner-btn banner-btn-secondary"
              {...(config.secondaryAction.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
            >
              {config.secondaryAction.label}
            </a>
          )}
        </div>
      )}

      {isDismissable && (
        <button
          onClick={handleDismiss}
          className="dismiss-btn"
          aria-label="Dismiss"
        >
          &times;
        </button>
      )}
    </div>
  );
};

export default StatusBanner;
